// Small floating flash-toast helper built on top of the v2 alert component.
//
// Usage (from a Stimulus controller or anywhere in app JS):
//
//   import { showFlashToast } from "../utils/flash_toast";
//   showFlashToast({ type: "success", message: "Group settings saved" });
//
//   showFlashToast({
//     type: "success",
//     message: "Changes saved",
//     action: { label: "Undo", onClick: () => undoLastSave() },
//   });
//
// Variants map to the existing `sprint-alert-v2--<variant>` classes.

import {
  createElement as createLucideElement,
  CircleAlert,
  CircleCheck,
  Info,
  TriangleAlert,
  Undo2,
  X,
} from "lucide";

const CONTAINER_ID = "sprint-flash-toasts";
const EXIT_CLASS = "sprint-flash-toast--exiting";
const VISIBLE_CLASS = "sprint-flash-toast--visible";
const FADE_OUT_MS = 200;
const MAX_VISIBLE_TOASTS = 3;
const restartDismissMap = new window.WeakMap();
const DEFAULT_DURATIONS = {
  success: 3500,
  info: 3500,
  warning: 5000,
  danger: 6000,
};

const VARIANT_ICONS = {
  success: { node: CircleCheck, name: "circle-check" },
  info: { node: Info, name: "info" },
  warning: { node: TriangleAlert, name: "triangle-alert" },
  danger: { node: CircleAlert, name: "circle-alert" },
};

function ensureContainer() {
  let container = document.getElementById(CONTAINER_ID);
  if (!container) {
    container = document.createElement("div");
    container.id = CONTAINER_ID;
    container.className = "sprint-flash-toasts";
    // High stacking so toasts sit above modals, dropdowns, etc.
    document.body.appendChild(container);
  }
  return container;
}

function removeToast(toast) {
  if (!toast || !toast.isConnected || toast.classList.contains(EXIT_CLASS)) {
    return;
  }

  let removed = false;
  const cleanup = () => {
    if (removed) {
      return;
    }
    removed = true;
    toast.removeEventListener("transitionend", onTransitionEnd);
    toast.remove();
  };

  const onTransitionEnd = (event) => {
    if (event.target !== toast || event.propertyName !== "opacity") {
      return;
    }
    cleanup();
  };

  toast.addEventListener("transitionend", onTransitionEnd);

  // Keep --visible so opacity stays at 1 until --exiting overrides it; rAF
  // ensures the browser has painted the visible state before exit starts.
  scheduleFrame(() => {
    toast.classList.add(EXIT_CLASS);
    // Belt-and-braces: if transitionend never fires (e.g. reduced motion),
    // still clean up so we don't leave orphan nodes hanging around.
    setTimeout(cleanup, FADE_OUT_MS + 50);
  });
}

function resolveVariant(type) {
  return Object.prototype.hasOwnProperty.call(DEFAULT_DURATIONS, type)
    ? type
    : "info";
}

// Icons are built synchronously so they always exist on first paint for dynamically
// inserted toasts — no page-load createIcons scan or window.lucide dependency.
function buildLucideIcon(iconNode, iconName) {
  return createLucideElement(iconNode, {
    class: `lucide lucide-${iconName}`,
    "aria-hidden": "true",
  });
}

// Schedule work on the next frame so layout/style changes can paint first.
// Falls back to setTimeout when requestAnimationFrame isn't available
// (e.g. jsdom without rAF shim).
function scheduleFrame(callback) {
  const raf = typeof window !== "undefined" && window.requestAnimationFrame;
  if (raf) {
    raf(callback);
  } else {
    setTimeout(callback, 0);
  }
}

function scheduleVisible(toast) {
  scheduleFrame(() => toast.classList.add(VISIBLE_CLASS));
}

function buildProgressBar() {
  const container = document.createElement("div");
  container.className = "sprint-alert-v2__progress";
  const fill = document.createElement("div");
  fill.className = "sprint-alert-v2__progress-fill";
  container.appendChild(fill);
  return { container, fill };
}

function startProgressFill(fill, durationMs) {
  fill.style.transition = "none";
  fill.style.width = "0%";
  void fill.offsetWidth;
  fill.style.transition = `width ${durationMs}ms linear`;
  scheduleFrame(() => {
    fill.style.width = "100%";
  });
}

function pauseProgressFill(fill) {
  const computed = window.getComputedStyle(fill);
  fill.style.transition = "none";
  fill.style.width = computed.width;
}

function resumeProgressFill(fill, remainingMs) {
  void fill.offsetWidth;
  fill.style.transition = `width ${remainingMs}ms linear`;
  scheduleFrame(() => {
    fill.style.width = "100%";
  });
}

function isVisibleToast(toast) {
  return toast.isConnected && !toast.classList.contains(EXIT_CLASS);
}

function getToastVariant(toast) {
  const match = toast.className.match(/sprint-alert-v2--(success|info|warning|danger)/);
  return match ? match[1] : null;
}

function getToastMessageText(toast) {
  const content = toast.querySelector(".sprint-alert-v2__content");
  return content ? content.textContent : "";
}

function findDuplicateToast(container, variant, message) {
  return Array.from(container.children).find((toast) => (
    isVisibleToast(toast)
    && getToastVariant(toast) === variant
    && getToastMessageText(toast) === message
  ));
}

function enforceMaxToasts(container) {
  const visible = Array.from(container.children).filter(isVisibleToast);
  while (visible.length >= MAX_VISIBLE_TOASTS) {
    removeToast(visible.shift());
  }
}

function pulseToast(toast) {
  if (toast.classList.contains(EXIT_CLASS)) {
    return;
  }
  toast.classList.remove(VISIBLE_CLASS);
  // Force reflow so re-adding the visible class retriggers the entrance transition.
  void toast.offsetWidth;
  scheduleVisible(toast);
}

function setupAutoDismiss(toast, dismiss, initialDelay) {
  if (!(initialDelay > 0)) {
    return () => {};
  }

  const { container: progressContainer, fill: progressFill } = buildProgressBar();
  toast.appendChild(progressContainer);

  let delay = initialDelay;
  let remaining = delay;
  let startedAt = Date.now();
  let timer = setTimeout(dismiss, remaining);

  startProgressFill(progressFill, delay);

  const clearTimer = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const restart = (newDelay) => {
    if (toast.classList.contains(EXIT_CLASS)) {
      return;
    }
    if (typeof newDelay === "number") {
      delay = newDelay;
    }
    clearTimer();
    remaining = delay;
    startedAt = Date.now();
    timer = setTimeout(dismiss, remaining);
    startProgressFill(progressFill, delay);
  };

  // Pause countdown while the user is hovering the toast — matches existing
  // notification-toasts controller behaviour. Track remaining time so repeated
  // hover cycles don't extend the toast lifetime beyond the intended duration.
  toast.addEventListener("mouseenter", () => {
    if (timer !== null) {
      clearTimer();
      remaining -= Date.now() - startedAt;
      pauseProgressFill(progressFill);
    }
  });
  toast.addEventListener("mouseleave", () => {
    if (timer === null && remaining > 0) {
      startedAt = Date.now();
      timer = setTimeout(dismiss, remaining);
      resumeProgressFill(progressFill, remaining);
    }
  });

  return restart;
}

function isValidAction(action) {
  return action
    && action.label
    && typeof action.onClick === "function";
}

function buildToastBody(title, message) {
  const body = document.createElement("div");
  body.className = "sprint-alert-v2__body";

  if (title) {
    const titleEl = document.createElement("h4");
    titleEl.className = "sprint-alert-v2__title";
    titleEl.textContent = title;
    body.appendChild(titleEl);
  }

  if (message) {
    const content = document.createElement("div");
    content.className = "sprint-alert-v2__content";
    content.textContent = message;
    body.appendChild(content);
  }

  return body;
}

const ACTION_BUTTON_CLASSES = "sprint-btn-v2 sprint-btn-v2-secondary sprint-btn-v2-sm sprint-flash-toast__action-btn";

function buildToastAction(action, dismiss) {
  const actionWrapper = document.createElement("div");
  actionWrapper.className = "sprint-alert-v2__action";

  const actionButton = document.createElement("button");
  actionButton.type = "button";
  actionButton.className = ACTION_BUTTON_CLASSES;
  actionButton.appendChild(buildLucideIcon(Undo2, "undo-2"));
  actionButton.appendChild(document.createTextNode(action.label));
  actionButton.addEventListener("click", () => {
    action.onClick();
    dismiss();
  });

  actionWrapper.appendChild(actionButton);
  return actionWrapper;
}

function buildDismissButton(dismiss) {
  const dismissButton = document.createElement("button");
  dismissButton.type = "button";
  dismissButton.className = "sprint-alert-v2__close";
  dismissButton.setAttribute("aria-label", "Dismiss");
  dismissButton.appendChild(buildLucideIcon(X, "x"));
  dismissButton.addEventListener("click", dismiss);
  return dismissButton;
}

function buildToastElement({ variant, title, message, action, dismiss }) {
  const toast = document.createElement("div");
  toast.className = [
    "sprint-alert-v2",
    `sprint-alert-v2--${variant}`,
    "sprint-flash-toast",
  ].join(" ");
  toast.setAttribute("role", variant === "danger" ? "alert" : "status");
  toast.setAttribute("aria-live", variant === "danger" ? "assertive" : "polite");

  const iconWrap = document.createElement("div");
  iconWrap.className = "sprint-alert-v2__icon";
  const variantIcon = VARIANT_ICONS[variant] || VARIANT_ICONS.info;
  iconWrap.appendChild(buildLucideIcon(variantIcon.node, variantIcon.name));
  toast.appendChild(iconWrap);
  toast.appendChild(buildToastBody(title, message));

  if (isValidAction(action)) {
    toast.appendChild(buildToastAction(action, dismiss));
  }

  toast.appendChild(buildDismissButton(dismiss));
  return toast;
}

export function showFlashToast({
  type = "success",
  message = "",
  title = "",
  duration,
  action,
} = {}) {
  if (!message && !title) {
    return null;
  }

  const variant = resolveVariant(type);
  const container = ensureContainer();
  const delay = typeof duration === "number" ? duration : DEFAULT_DURATIONS[variant];

  const existing = findDuplicateToast(container, variant, message);
  if (existing) {
    const restart = restartDismissMap.get(existing);
    if (typeof restart === "function") {
      restart(delay);
    }
    pulseToast(existing);
    return existing;
  }

  enforceMaxToasts(container);

  const dismiss = () => removeToast(toast);
  const toast = buildToastElement({ variant, title, message, action, dismiss });

  container.appendChild(toast);

  // Trigger the slide/fade-in on the next frame so the initial class takes
  // effect before we flip to the "visible" state.
  scheduleVisible(toast);

  restartDismissMap.set(toast, setupAutoDismiss(toast, dismiss, delay));

  return toast;
}

export default showFlashToast;
